import {Action} from 'platform/components';
import {HStack, Show, Text} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNotNil} from 'ramda';

import {useGetUserQuery} from '@dms/api/customer';
import {ListCampaignResponseBody} from '@dms/api/sales';
import {DraggableCard} from '@dms/features/dnd';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {getUserName, SalesCampaignStatusFlag} from '@dms/shared';

import {buildArray, parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

const DATE_RANGE_SEPARATOR = '–';

interface CampaignCardProps extends RequiredTestIdProps {
  campaign: ListCampaignResponseBody;
  handleProps?: React.HTMLAttributes<HTMLDivElement>;
  onEdit: (campaignId: string) => void;
  onDelete: (campaign: ListCampaignResponseBody) => void;
}

export function CampaignCard(props: CampaignCardProps) {
  const formatDateTime = useDateTimeFormatter();
  const {data: owner} = useGetUserQuery({id: props.campaign.ownerId});

  const validFrom = parseDate(props.campaign.validFrom);
  const validTo = parseDate(props.campaign.validTo);

  const isDimmed = props.campaign.status === 'ENDED';

  const validity = [validFrom, validTo]
    .map((date) => formatDateTime('dateShort', date))
    .join(` ${DATE_RANGE_SEPARATOR} `);

  const ownerName = getUserName(owner) ?? owner?.email;

  const campaignTestId = `campaign-${props.campaign.id}`;

  return (
    <DraggableCard
      title={props.campaign.name}
      handleProps={props.handleProps}
      variant="inlineGrey"
      isDimmed={isDimmed}
      data-testid={suffixTestId(campaignTestId, props)}
      additionalContent={
        <HStack spacing={4} align="center" wrap>
          <SalesCampaignStatusFlag
            status={props.campaign.status}
            data-testid={suffixTestId(`${campaignTestId}-status`, props)}
          />

          <Text size="small" color="secondary">
            {validity}
          </Text>

          <Show when={isNotNil(ownerName)}>
            <Text size="small" color="secondary">
              {`${i18n.t('entity.salesCampaign.labels.owner')}: ${ownerName}`}
            </Text>
          </Show>
        </HStack>
      }
      actions={buildArray<Action>()
        .add({
          type: 'iconButton',
          icon: 'image/edit',
          onClick: () => props.onEdit(props.campaign.id),
          'data-testid': testIds.settings.campaigns(`editCampaign-${props.campaign.id}`),
        })
        .add({
          type: 'iconButton',
          icon: 'action/delete',
          severity: 'danger',
          onClick: () => props.onDelete(props.campaign),
          'data-testid': testIds.settings.campaigns(`deleteCampaign-${props.campaign.id}`),
        })}
    />
  );
}
